import dayjs from 'dayjs';

/**
 * The Books shell's date window: what it defaults to, and how it is remembered.
 *
 * The window means two different things depending on the tab — **movement**
 * over it (Journal, VAT report) or a **balance as of** its end (Chart of
 * accounts, Trial balance). That is stated per tab in the subtitles rather than
 * left for a reader to infer; see `booksWindowMeta`.
 */

/** ISO `YYYY-MM-DD`. */
export type BooksWindow = { from: string; to: string };

/**
 * The day of the month up to which "the month being worked on" is still the
 * *previous* one.
 *
 * Georgian VAT is filed by the 15th, so on any day 1–15 an accountant opening
 * Books is almost always working on last month — reconciling it, settling its
 * VAT, answering a question about it. Defaulting to month-to-date meant that on
 * the 2nd they got a two-day window: Journal opened on "0 entries" and the VAT
 * report showed zeros with a disabled settle button, with nothing on screen
 * saying the picker was the reason (BOS-166).
 */
export const FILING_DAY = 15;

/**
 * The window Books opens on when the URL says nothing and nothing is
 * remembered.
 *
 * On or before the {@link FILING_DAY}, the previous whole month. After it, the
 * current month **to today** — not to month-end: the trial-balance tab reads
 * `to` as its `asOf`, and asking the server for a statement dated in the future
 * quietly folds future-dated entries into "the current position".
 */
export const defaultBooksWindow = (today: dayjs.Dayjs): BooksWindow => {
    if (today.date() <= FILING_DAY) {
        const previous = today.subtract(1, 'month');
        return {
            from: previous.startOf('month').format('YYYY-MM-DD'),
            to: previous.endOf('month').format('YYYY-MM-DD'),
        };
    }
    return {
        from: today.startOf('month').format('YYYY-MM-DD'),
        to: today.format('YYYY-MM-DD'),
    };
};

/**
 * The month a VAT return filed today covers.
 *
 * Always the previous calendar month — a return is filed after the period it
 * reports on, never during it. The Books shell's own window may be anything;
 * this is the one the VAT tab means when it says "the period due on the 15th".
 */
export const vatFilingWindow = (today: dayjs.Dayjs): BooksWindow => {
    const previous = today.subtract(1, 'month');
    return {
        from: previous.startOf('month').format('YYYY-MM-DD'),
        to: previous.endOf('month').format('YYYY-MM-DD'),
    };
};

/** When that return is due: the 15th of the month after the period. */
export const vatFilingDueDate = (today: dayjs.Dayjs): string =>
    today.startOf('month').date(FILING_DAY).format('YYYY-MM-DD');

const STORAGE_KEY = 'bos.accounting.books.window';

const isIsoDate = (value: unknown): value is string =>
    typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value);

/**
 * The window the user last chose explicitly, or `null`.
 *
 * Remembered so a reload does not throw away a deliberate choice — an
 * accountant who widened to a quarter and refreshed used to land back on the
 * default. Only an *explicit* change is stored: a default is not a choice, and
 * storing it would freeze the shell on whatever month it first opened.
 *
 * Every read is guarded: a private window, cleared site data, or a browser
 * blocking storage all throw rather than return nothing, and none of them is a
 * reason to fail to render a page.
 */
export const readRememberedWindow = (): BooksWindow | null => {
    try {
        const raw = window.localStorage.getItem(STORAGE_KEY);
        if (!raw) {
            return null;
        }
        const parsed: unknown = JSON.parse(raw);
        if (
            typeof parsed === 'object' &&
            parsed !== null &&
            isIsoDate((parsed as BooksWindow).from) &&
            isIsoDate((parsed as BooksWindow).to)
        ) {
            return parsed as BooksWindow;
        }
        return null;
    } catch {
        return null;
    }
};

export const rememberWindow = (windowValue: BooksWindow): void => {
    try {
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify(windowValue));
    } catch {
        // Storage is a convenience here, never a requirement.
    }
};
